import { chunkText, pickVoice } from './speech'
import { activatePlaybackSession, browserAudioSession, type AudioSessionLike } from './audioSession'

interface Playback {
  blocks: string[]
  start: number
  lang: string
  rate: number
  onBlock: (index: number) => void
  onStart: () => void
  onComplete: () => void
  onError: (message: string) => void
}

type Synthesis = Pick<SpeechSynthesis, 'speak' | 'cancel' | 'resume' | 'paused' | 'getVoices'>

/** Owns the utterance until completion (Safari may otherwise collect it).
 * Never defer the first speak(): mobile browsers require the user's tap.
 */
export class SpeechPlayback {
  private releaseAudioSession: (() => void) | null = null
  private generation = 0
  private utterance: SpeechSynthesisUtterance | null = null
  private startupTimer: ReturnType<typeof setTimeout> | undefined

  constructor(
    private synth: Synthesis,
    private createUtterance = (text: string) => new SpeechSynthesisUtterance(text),
    private audioSession: AudioSessionLike | null = browserAudioSession(),
  ) {}

  stop() {
    this.generation++
    clearTimeout(this.startupTimer)
    this.utterance = null
    this.synth.cancel()
    this.releaseAudioSession?.()
    this.releaseAudioSession = null
  }

  play(options: Playback) {
    this.stop()
    this.releaseAudioSession = activatePlaybackSession(this.audioSession)
    const generation = this.generation
    // Some mobile engines remain paused even after cancel().
    if (this.synth.paused) this.synth.resume()
    let block = options.start
    let chunk = 0
    let chunks = chunkText(options.blocks[block] ?? '')
    options.onBlock(block)

    const fail = () => {
      if (generation !== this.generation) return
      this.stop()
      options.onError('Audio could not start. Check your device volume and tap Retry. Your browser may not have a reading voice available.')
    }
    const speakNext = () => {
      if (generation !== this.generation) return
      while (chunk >= chunks.length) {
        block++
        if (block >= options.blocks.length) {
          clearTimeout(this.startupTimer)
          this.utterance = null
          this.releaseAudioSession?.()
          this.releaseAudioSession = null
          options.onComplete()
          return
        }
        chunk = 0
        chunks = chunkText(options.blocks[block])
        options.onBlock(block)
      }
      const utterance = this.createUtterance(chunks[chunk++])
      this.utterance = utterance
      utterance.lang = options.lang
      utterance.rate = options.rate
      // Voices can be empty at first. Use the default immediately; consult
      // the fresh voice list on subsequent chunks, without a timer/await.
      const voice = pickVoice(this.synth.getVoices(), options.lang)
      if (voice) utterance.voice = voice
      utterance.onstart = () => {
        if (generation !== this.generation || this.utterance !== utterance) return
        clearTimeout(this.startupTimer)
        options.onStart()
      }
      utterance.onend = () => {
        if (generation !== this.generation || this.utterance !== utterance) return
        this.utterance = null
        clearTimeout(this.startupTimer)
        speakNext()
      }
      utterance.onerror = () => {
        if (this.utterance === utterance) fail()
      }
      clearTimeout(this.startupTimer)
      this.startupTimer = setTimeout(fail, 10_000)
      try {
        this.synth.speak(utterance)
      } catch {
        fail()
      }
    }
    speakNext()
  }
}
